import { api } from "./api";

export const REASONS = [
    { value: "missed_check_in_out", label: "Missed check-in / check-out" },
    { value: "device_agent_internet_issue", label: "Device / agent / internet issue" },
    { value: "client_visit_official_duty", label: "Client visit / official duty" },
    { value: "approved_wfh", label: "Approved work from home" },
    { value: "meeting_training_offline_work", label: "Meeting / training / offline work" },
    { value: "other", label: "Other" },
];

export const REQUEST_TYPES = [
    { value: "full_day_attendance", label: "Full day worked", fields: [] },
    { value: "half_day_attendance", label: "Half day worked", fields: [] },
    { value: "time_correction", label: "Correct check-in / check-out times", fields: ["check_in_time", "check_out_time"] },
    { value: "missing_check_out", label: "Missing check-out", fields: ["check_out_time"] },
    { value: "missing_check_in", label: "Missing check-in", fields: ["check_in_time"] },
    { value: "late_arrival_excuse", label: "Late arrival excuse", fields: ["check_in_time"] },
    { value: "early_departure_excuse", label: "Early departure excuse", fields: ["check_out_time"] },
    { value: "missing_break_end", label: "Missing break end", fields: ["break_end_time", "break_name"] },
    { value: "break_correction", label: "Break correction", fields: ["break_end_time", "break_name"] },
];

export const TYPE_LABEL = Object.fromEntries(REQUEST_TYPES.map((t) => [t.value, t.label]));

export const REQ_STATUS_CLS = {
    Draft: "bg-slate-100 text-slate-600",
    Pending: "bg-amber-100 text-amber-800",
    Approved: "bg-emerald-100 text-emerald-700",
    Rejected: "bg-rose-100 text-rose-700",
    Withdrawn: "bg-slate-100 text-slate-500",
};

export const MONTH_STATUS_CLS = {
    Provisional: "bg-sky-50 text-sky-800 border-sky-200",
    "Awaiting Approval": "bg-amber-50 text-amber-800 border-amber-200",
    Finalised: "bg-emerald-50 text-emerald-800 border-emerald-200",
};

export const HISTORY_LABEL = {
    drafted: "Saved as draft",
    draft_edited: "Draft edited",
    submitted: "Submitted",
    stage_approved: "Stage approved",
    approved: "Approved",
    modified: "Approved with changes",
    rejected: "Rejected",
    withdrawn: "Withdrawn",
    override_approved: "Super Admin override — approved",
    override_modified: "Super Admin override — changed",
    override_rejected: "Super Admin override — rejected",
    resolved_by_approver: "Resolved by approver",
};

export const EVIDENCE_ACCEPT = ".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png";
export const EVIDENCE_MAX = 5 * 1024 * 1024;

// "HH:MM" entered in IST on dateStr → UTC ISO string.
export function istToUtcIso(hhmm, dateStr) {
    if (!hhmm || !dateStr) return null;
    return new Date(`${dateStr}T${hhmm}:00+05:30`).toISOString();
}

// UTC ISO string → "HH:MM" in IST.
export function utcIsoToIstHHMM(iso) {
    if (!iso) return "";
    return new Date(new Date(iso).getTime() + 5.5 * 3600 * 1000).toISOString().slice(11, 16);
}

export function fmtHours(h) {
    if (h == null) return "—";
    const sign = h < 0 ? "-" : "";
    const abs = Math.abs(h);
    const hh = Math.floor(abs);
    const mm = Math.round((abs - hh) * 60);
    return `${sign}${hh}h ${String(mm).padStart(2, "0")}m`;
}

// Recorded hours, plus hours credited by an approved full/half-day regularisation.
export function dayHoursLabel(recordedMin, creditedMin, empty = "—") {
    const rec = (recordedMin || 0) / 60;
    const cr = (creditedMin || 0) / 60;
    if (cr > 0) return rec > 0 ? `${fmtHours(rec)} + ${fmtHours(cr)} (credited)` : `${fmtHours(cr)} (credited)`;
    return rec > 0 ? fmtHours(rec) : empty;
}

export function periodLabel(period) {
    if (!period) return "";
    const [y, m] = period.split("-").map(Number);
    return new Date(y, m - 1, 1).toLocaleString("en-IN", { month: "long", year: "numeric" });
}

export function shiftPeriod(period, delta) {
    const [y, m] = period.split("-").map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export function currentPeriodIST() {
    const now = new Date(Date.now() + 5.5 * 3600 * 1000);
    return now.toISOString().slice(0, 7);
}

export async function uploadEvidence(file) {
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    if (!["pdf", "jpg", "jpeg", "png"].includes(ext)) throw new Error("Evidence must be a PDF, JPG or PNG file.");
    if (file.size > EVIDENCE_MAX) throw new Error("Evidence file must be 5 MB or smaller.");
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/regularization/upload-evidence", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 60000,
    });
    return data;
}

export async function openEvidence(objectId) {
    const win = window.open("", "_blank");
    try {
        const { data, headers } = await api.get(`/regularization/evidence/${objectId}`, { responseType: "blob" });
        const url = URL.createObjectURL(new Blob([data], { type: headers["content-type"] }));
        if (win) { win.opener = null; win.location.href = url; } else {
            const a = document.createElement("a");
            a.href = url;
            a.download = `evidence-${objectId}`;
            a.click();
        }
        setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
        if (win) win.close();
        throw err;
    }
}
